import { IFilterCalibrationAnalysis } from "@app/types/calibration.types";
import {
  CalibrationPatternSourceType,
  CalibrationTritioResponse,
  CurvaTypes,
} from "@app/types/report.types";
import { normalizeDate, toExponentialUppercase } from "@app/utils/functions";
import dayjs from "dayjs";
import { calcularU2relFa, recalcularCoeficientes } from "./calculations";
import { UserType } from "@app/types/genericTables.types";
import { CurvaRow } from "./curva.rows";
import { MathArray } from "mathjs";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import { hasRowCurva } from "../editCalibrations";
import { useAdjustmentCurve } from "./useAdjustmentCurve";
import { COEFFICIENTS_DECIMALS } from "./constants";

export const calculateFcCalc = (coef_1, coef_2, coef_3, coef_4, coef_5, x) => {
  return (
    coef_1 * Math.pow(x, 4) +
    coef_2 * Math.pow(x, 3) +
    coef_3 * Math.pow(x, 2) +
    coef_4 * x +
    coef_5
  );
};

export const calculateFcCalcAlpha = (
  coef_1: number,
  coef_2: number,
  coef_3: number,
  x: number
) => {
  return coef_1 * Math.pow(x, 2) + coef_2 * x + coef_3;
};

export const calculateEfficiency = (newRow: any, rowsCurva: any) => {
  const updatedRows = rowsCurva.map((row: any) =>
    row.id === newRow.id ? { ...row, ...newRow } : row
  );
  const firstValue = updatedRows[0].efficiency;
  const recalculatedRows = updatedRows.map((row: any, index: any) => ({
    ...row,
    fa_exp: index === 0 ? 1 : firstValue ? row.efficiency / firstValue : 0,
    fc_calc: 0,
  }));
  return recalculatedRows;
};

export const calculateEfficiencyAlpha = (newRow: any, rowsCurva: any) => {
  const updatedRows = rowsCurva.map((row: any) =>
    row.id === newRow.id ? { ...row, ...newRow } : row
  );
  const firstValue = updatedRows[0].efficiency;

  const recalculatedRows = updatedRows.map((row: any, index: any) => ({
    ...row,
    fa_exp: index === 0 ? 1 : firstValue ? row.efficiency / firstValue : 0,
    fc_calc: 0,
  }));
  return recalculatedRows;
};

export const calculateCustomValue = (rows: any) => {
  const completeRows = rows.filter(
    (row: any) => row.fa_exp !== 0 && row.fc_calc !== 0
  );

  const sum = completeRows.reduce((acc: any, row: any) => {
    const diffSquared = Math.pow(row.fa_exp - row.fc_calc, 2);
    const squaredDivided = diffSquared / Math.pow(row.fa_exp, 2);
    return acc + squaredDivided;
  }, 0);

  return (1 / completeRows.length) * sum;
};

export const areAllRowsComplete = (rows: any) => {
  return rows.every((row: any) => row.fa_exp !== 0 && row.fc_calc !== 0);
};

export const efficienciesMapping = (efficiency) => {   
  const result = {
    analyst: efficiency.analyst.id,
    x: efficiency.x !== undefined && efficiency.x !== null && efficiency.x !== "" ? Number(efficiency.x) : null,
    x2: efficiency.x2 !== undefined && efficiency.x2 !== null && efficiency.x2 !== "" ? Number(efficiency.x2) : null,
    x3: efficiency.x3 !== undefined && efficiency.x3 !== null && efficiency.x3 !== "" ? Number(efficiency.x3) : null,
    x4: efficiency.x4 !== undefined && efficiency.x4 !== null && efficiency.x4 !== "" ? Number(efficiency.x4) : null,
    a: efficiency.a !== undefined && efficiency.a !== null && efficiency.a !== "" ? Number(efficiency.a) : null,
    date: efficiency.date,
    equipment: efficiency?.equipment.id,
    internal_comments: efficiency.internal_comments,
    efficiency_type: efficiency.efficiency_type.key,
    analysis: efficiency.analysis.map(a => a.id),
    part: efficiency.part && Array.isArray(efficiency.part) && efficiency.part.length > 0
      ? efficiency.part.map(p => p.parte || p.part_name).join(", ")
      : null,
    modified_record: !!efficiency.id || false,
    cs_source_activity_bq: efficiency.cs_source_activity_bq
      ? parseFloat(efficiency.cs_source_activity_bq)
      : undefined,
    date_activity: efficiency.date_activity
      ? efficiency.date_activity
      : undefined,
    cs_gpd: efficiency.cs_gpd ? parseFloat(efficiency.cs_gpd) : undefined,
    cs_decay_constant: efficiency.cs_decay_constant
      ? parseFloat(efficiency.cs_decay_constant)
      : undefined,
    cs_source_geometry: efficiency.cs_source_geometry
      ? efficiency.cs_source_geometry.id
      : undefined,
    date_cs_source_counting: efficiency.date_cs_source_counting
      ? efficiency.date_cs_source_counting
      : undefined,
    detector: efficiency.detector ? efficiency.detector.id : undefined,
    cs_source_counting_time: efficiency.cs_source_counting_time
      ? parseFloat(efficiency.cs_source_counting_time)
      : undefined,
    cs_time_increment_from_counting_date:
      efficiency.cs_time_increment_from_counting_date
        ? parseFloat(efficiency.cs_time_increment_from_counting_date)
        : undefined,
    cs_source_gps: efficiency.cs_source_gps
      ? efficiency.cs_source_gps
      : undefined,
    cs_source_pattern_count: efficiency.cs_source_pattern_count
      ? parseFloat(efficiency.cs_source_pattern_count)
      : undefined,
    cs_fondo_count_1: efficiency.cs_fondo_count_1
      ? parseFloat(efficiency.cs_fondo_count_1)
      : undefined,
    cs_fondo_counting_time_1: efficiency.cs_fondo_counting_time_1
      ? parseFloat(efficiency.cs_fondo_counting_time_1)
      : undefined,
    cs_fondo_count_2: efficiency.cs_fondo_count_2
      ? parseFloat(efficiency.cs_fondo_count_2)
      : undefined,
    cs_fondo_counting_time_2: efficiency.cs_fondo_counting_time_2
      ? parseFloat(efficiency.cs_fondo_counting_time_2)
      : undefined,
    avg_integral_fondo: efficiency.avg_integral_fondo
      ? parseFloat(efficiency.avg_integral_fondo)
      : undefined,
    cs_source_pattern_net_area: efficiency.cs_source_pattern_net_area
      ? parseFloat(efficiency.cs_source_pattern_net_area)
      : undefined,
    cs_source_pattern_efficiency: efficiency.cs_source_pattern_efficiency
      ? parseFloat(efficiency.cs_source_pattern_efficiency)
      : undefined,
  };
  
  return result;
};

export function mapEfficiencyParts(efficiency) {
  
  if (!efficiency.parts) return [];
  const partsArray = Array.isArray(efficiency.parts)
    ? efficiency.parts
    : Object.values(efficiency.parts);
  
  // Get the part array from the form
  const partNamesArray = efficiency.part && Array.isArray(efficiency.part) 
    ? efficiency.part 
    : [];
  
  const isEdit = !!efficiency.id;
  
  // Si es EDICIÓN: crear una sola eficiencia con todas las partes concatenadas
  if (isEdit) {
    const allPartNames = partNamesArray
      .map(p => p.parte || p.part_name)
      .filter(name => name)
      .join(", ");
    
    const firstPart = partsArray.find(p => p !== null) || {};
    
    return [{
      analyst: efficiency.analyst.id,
      x: firstPart.x !== undefined && firstPart.x !== null && firstPart.x !== "" ? Number(firstPart.x) : null,
      x2: firstPart.x2 !== undefined && firstPart.x2 !== null && firstPart.x2 !== "" ? Number(firstPart.x2) : null,
      x3: firstPart.x3 !== undefined && firstPart.x3 !== null && firstPart.x3 !== "" ? Number(firstPart.x3) : null,
      x4: firstPart.x4 !== undefined && firstPart.x4 !== null && firstPart.x4 !== "" ? Number(firstPart.x4) : null,
      a: firstPart.a !== undefined && firstPart.a !== null && firstPart.a !== "" ? Number(firstPart.a) : null,
      efficiency: firstPart.efficiency && firstPart.efficiency !== "" ? Number(firstPart.efficiency) : null,
      date: efficiency.date,
      equipment: efficiency.equipment.id,
      internal_comments: efficiency.internal_comments,
      efficiency_type: efficiency.efficiency_type.key,
      part: allPartNames || null,
      analysis: efficiency.analysis.map(a => a.id),
      modified_record: true,
    }];
  }
  
  // Si es CREACIÓN: crear una eficiencia por cada parte seleccionada
  const result = partNamesArray.map((partItem) => {
    const partId = partItem.id; // "part-0", "part-1", etc.
    const partData = efficiency.parts[partId] || {};
    
    return {
      analyst: efficiency.analyst.id,
      x: partData.x !== undefined && partData.x !== null && partData.x !== "" ? Number(partData.x) : null,
      x2: partData.x2 !== undefined && partData.x2 !== null && partData.x2 !== "" ? Number(partData.x2) : null,
      x3: partData.x3 !== undefined && partData.x3 !== null && partData.x3 !== "" ? Number(partData.x3) : null,
      x4: partData.x4 !== undefined && partData.x4 !== null && partData.x4 !== "" ? Number(partData.x4) : null,
      a: partData.a !== undefined && partData.a !== null && partData.a !== "" ? Number(partData.a) : null,
      efficiency: partData.efficiency && partData.efficiency !== "" ? Number(partData.efficiency) : null,
      date: efficiency.date,
      equipment: efficiency.equipment.id,
      internal_comments: efficiency.internal_comments,
      efficiency_type: efficiency.efficiency_type.key,
      part: partItem.parte || partItem.part_name, // Nombre individual de la parte
      analysis: efficiency.analysis.map(a => a.id),
      modified_record: false,
    };
  });
    
  return result;
}

export function mapCalibrationTritium(
  inputObj: any
): CalibrationTritioResponse {
  // Manejar typeAnalysis como array o objeto individual
  const typeAnalysisArray = Array.isArray(inputObj.typeAnalysis) 
    ? inputObj.typeAnalysis 
    : [inputObj.typeAnalysis];
  
  // Para la lógica que requiere un solo tipo de análisis, usar el primero
  const firstTypeAnalysis = typeAnalysisArray[0];

  // Verificar si es BT o BR (ambos tienen el mismo comportamiento para tfact)
  const isBetaAnalysis = firstTypeAnalysis.codigo === KeysTabsAnalysis.BT || 
                         firstTypeAnalysis.codigo === KeysTabsAnalysis.BR;

  const setTfact_aa =
    isBetaAnalysis
      ? parseFloat(
          inputObj.uncertainty_calibration_source_counting_time_without_extinguishing_agent
        )
      : inputObj["rowsCurva-results"]?.u2relFa
      ? parseFloat(inputObj["rowsCurva-results"]?.u2relFa)
      : inputObj.tfact_aa
      ? parseFloat(inputObj.tfact_aa)
      : 0;

  const setTfact_aa_k_40 =
    isBetaAnalysis
      ? parseFloat(
          inputObj.uncertainty_calibration_source_counting_time_without_extinguishing_agent_k_40
        )
      : inputObj["rowsCurva-results"]?.u2relFa
      ? parseFloat(inputObj["rowsCurva-results"].u2relFa)
      : inputObj.tfact_aa_k_40
      ? parseFloat(inputObj.tfact_aa_k_40)
      : 0;

  const outputObj: CalibrationTritioResponse = {
    date: inputObj.date,
    analysis_type: typeAnalysisArray.map(type => type.id), // Enviar array de IDs
    equipment: inputObj?.equipment?.id,
    detector: inputObj?.detector?.id,
    date_detector: inputObj.date_detector,
    mean_efficiency: inputObj.mean_efficiency,
    ta0: parseFloat(inputObj.ta0),
    tua0: parseFloat(inputObj.tua0),
    tmpp: parseFloat(inputObj.tmpp),
    tmps: parseFloat(inputObj.tmps),
    tmpsfcal: parseFloat(inputObj.tmpsfcal),
    tdpmg: parseFloat(inputObj.tdpmg),
    tef: parseFloat(inputObj.tef),
    tertiary_patron_mass: inputObj.tertiary_patron_mass
      ? parseFloat(inputObj.tertiary_patron_mass)
      : undefined,
    calibration_source_counting_time_without_extinguishing_agent:
      inputObj.calibration_source_counting_time_without_extinguishing_agent
        ? parseFloat(
            inputObj.calibration_source_counting_time_without_extinguishing_agent
          )
        : undefined,
    tsie_sqp_patron_without_agent_extintor:
      inputObj.tsie_sqp_patron_without_agent_extintor
        ? parseFloat(inputObj.tsie_sqp_patron_without_agent_extintor)
        : undefined,
    tcpmps: parseFloat(inputObj.tcpmps),
    tcal: parseInt(inputObj.tcal),
    tfact_aa: setTfact_aa,
    tfact_aa_k_40: setTfact_aa_k_40,
    tgrado: 4,
    tnpoints: 0,
    terror: parseFloat(inputObj.results?.coefficients?.r2),
    coe0: parseFloat(inputObj.results?.coefficients?.independent),
    coe1: parseFloat(inputObj.results?.coefficients?.grade1),
    coe2: parseFloat(inputObj.results?.coefficients?.grade2),
    coe3: parseFloat(inputObj.results?.coefficients?.grade3),
    coe4: parseFloat(inputObj.results?.coefficients?.grade4),
    terror_k_40: parseFloat(inputObj.results?.coefficients?.r2),
    coe0_k_40: parseFloat(inputObj.results?.coefficients?.independent),
    coe1_k_40: parseFloat(inputObj.results?.coefficients?.grade1),
    coe2_k_40: parseFloat(inputObj.results?.coefficients?.grade2),
    coe3_k_40: parseFloat(inputObj.results?.coefficients?.grade3),
    coe4_k_40: parseFloat(inputObj.results?.coefficients?.grade4),
    modified_record: false,
    internal_comments: inputObj.internal_comments || null,
    analyst: inputObj.analyst.id,
    cpm_of_the_alpha_pattern_in_the_beta_win:
      inputObj.cpm_of_the_alpha_pattern_in_the_beta_win
        ? parseFloat(inputObj.cpm_of_the_alpha_pattern_in_the_beta_win)
        : undefined,
    cpm_of_the_alpha_pattern_in_the_alfa_win:
      inputObj.cpm_of_the_alpha_pattern_in_the_alfa_win
        ? parseFloat(inputObj.cpm_of_the_alpha_pattern_in_the_alfa_win)
        : undefined,
    total_counting_time_alpha_pattern_in_total_detectors:
      inputObj.total_counting_time_alpha_pattern_in_total_detectors
        ? parseFloat(
            inputObj.total_counting_time_alpha_pattern_in_total_detectors
          )
        : undefined,
    vol_of_sec_in_calibration_source_at_0:
      inputObj.vol_of_sec_in_calibration_source_at_0
        ? parseFloat(inputObj.vol_of_sec_in_calibration_source_at_0)
        : undefined,
    sec_pattern_activity_at_0_cal_s_at_count_d:
      inputObj.sec_pattern_activity_at_0_cal_s_at_count_d
        ? parseFloat(inputObj.sec_pattern_activity_at_0_cal_s_at_count_d)
        : undefined,
    cal_source_efficiency_at_0: inputObj.cal_source_efficiency_at_0
      ? parseFloat(inputObj.cal_source_efficiency_at_0)
      : undefined,
    cpm_of_cal_source_with_quantity_at_0:
      inputObj.cpm_of_cal_source_with_quantity_at_0
        ? parseFloat(inputObj.cpm_of_cal_source_with_quantity_at_0)
        : undefined,
    cal_source_counting_time_to_0_in_total_detectors:
      inputObj.cal_source_counting_time_to_0_in_total_detectors
        ? parseFloat(inputObj.cal_source_counting_time_to_0_in_total_detectors)
        : undefined,
    mass_of_the_torta_of_fc_with_the_lowest_amount:
      inputObj.mass_of_the_torta_of_fc_with_the_lowest_amount
        ? parseFloat(inputObj.mass_of_the_torta_of_fc_with_the_lowest_amount)
        : undefined,
    efficiency_of_the_torta_of_fc_with_the_lowest_amount:
      inputObj.efficiency_of_the_torta_of_fc_with_the_lowest_amount
        ? parseFloat(
            inputObj.efficiency_of_the_torta_of_fc_with_the_lowest_amount
          )
        : undefined,
    cpm_of_the_torta_of_fc_with_the_lowest_amount:
      inputObj.cpm_of_the_torta_of_fc_with_the_lowest_amount
        ? parseFloat(inputObj.cpm_of_the_torta_of_fc_with_the_lowest_amount)
        : undefined,
    total_counting_time_of_the_torta_of_fc_with_the_lowest_amount:
      inputObj.total_counting_time_of_the_torta_of_fc_with_the_lowest_amount
        ? parseFloat(
            inputObj.total_counting_time_of_the_torta_of_fc_with_the_lowest_amount
          )
        : undefined,
    date_calibration_certificate: inputObj.date_calibration_certificate
      ? inputObj.date_calibration_certificate
      : undefined,
    identity_pattern_source: inputObj.identity_pattern_source
      ? inputObj.identity_pattern_source
      : undefined,
    part: inputObj.part ? inputObj?.part?.id : undefined,
    rowsCurvaPreload: inputObj.rowsCurvaPreload
      ? inputObj.rowsCurvaPreload
      : undefined,
    rowsCurvaK40Preload: inputObj.rowsCurvaK40Preload
      ? inputObj.rowsCurvaK40Preload
      : undefined,
  };

  let rowsCurveAb: CurvaTypes[] = [];
  let rowsCurveAbsK40: CurvaTypes[] = [];
  let validRowIndex = 0;
  
  // Validar que rowsCurva exista y sea un array antes de iterar
  if (inputObj.rowsCurva && Array.isArray(inputObj.rowsCurva)) {
    inputObj.rowsCurva.forEach((row) => {
      // Guardar si tiene datos válidos (al menos sqp_tsie_quenching y fa_exp)
      if (row.sqp_tsie_quenching !== "" && row.sqp_tsie_quenching !== null && 
          row.fa_exp !== "" && row.fa_exp !== null) {
        const x = parseFloat(row.sqp_tsie_quenching);
        const e = parseFloat(row.efficiency);
        const c = row.fc_calc || 0;
        const y = row.fa_exp;
        const id = row.id;

        rowsCurveAb.push({ x, e, c, y, id, is_k40: false });
        validRowIndex++;
      }
    });
  }

  let validRowIndex_k_40 = 0;
  // Validar que rowsCurvaK40 exista y sea un array antes de iterar
  if (inputObj.rowsCurvaK40 && Array.isArray(inputObj.rowsCurvaK40)) {
    inputObj.rowsCurvaK40.forEach((row) => {
      // Guardar si tiene datos válidos (al menos sqp_tsie_quenching y fa_exp)
      if (row.sqp_tsie_quenching !== "" && row.sqp_tsie_quenching !== null && 
          row.fa_exp !== "" && row.fa_exp !== null) {
        const x = parseFloat(row.sqp_tsie_quenching);
        const e = parseFloat(row.efficiency);
        const c = row.fc_calc || 0;
        const y = row.fa_exp;
        const id = row.id;
        rowsCurveAbsK40.push({ x, e, c, y, id, is_k40: true });
        validRowIndex_k_40++;
      }
    });
  }

  outputObj.tnpoints = validRowIndex;
  outputObj.tnpoints_k_40 = validRowIndex_k_40;
  outputObj.rowsCurveAbs = rowsCurveAb;
  outputObj.rowsCurveAbsK40 = rowsCurveAbsK40;

  return outputObj;
}

export async function mapCalibrationPreloadToRowsCurva(idCalibration) {
  const preload = await useAdjustmentCurve
    .getState()
    .fetchPreloadAdjustmentCurve(idCalibration);

  if (!preload || preload.length === 0) {
    return [];
  }

  const formattedData = preload
    .filter((item) => item.is_k40 === false)
    .map((item) => ({
      sqp_tsie_quenching: item.x,
      efficiency: item.e,
      fc_calc: item.c,
      fa_exp: item.y,
      id: item.id,
    }));

  return formattedData;
}

export async function mapCalibrationPreloadToRowsCurvaK40(idCalibration) {
  const preload = await useAdjustmentCurve
    .getState()
    .fetchPreloadAdjustmentCurve(idCalibration);

  const formattedData = preload
    .filter((item) => item.is_k40 === true)
    .map((item) => ({
      sqp_tsie_quenching: item.x,
      efficiency: item.e,
      fc_calc: item.c,
      fa_exp: item.y,
      id: item.id,
    }));

  return formattedData;
}

export function filter(
  filters: IFilterCalibrationAnalysis,
  calibrationList: CalibrationTritioResponse[]
) {
  const { date_to, date_from, equipment, analysis_type, part } = filters;

  return calibrationList.filter((calibration) => {
    const sampleEmissionDate = normalizeDate(new Date(calibration.date));

    const filterDateStart = date_from
      ? normalizeDate(new Date(date_from))
      : null;
    const filterDateEnd = date_to ? normalizeDate(new Date(date_to)) : null;

    const dateInRange =
      (!filterDateStart || sampleEmissionDate >= filterDateStart) &&
      (!filterDateEnd || sampleEmissionDate <= filterDateEnd);

    const equipmentFilter = equipment && equipment.length > 0;
    const equipmentMatch = equipmentFilter
      ? calibration.equipment &&
        equipment.some((e) => e.id === calibration.equipment?.id)
      : true;

    const analysisTypeFilter = analysis_type && analysis_type.length > 0;
    const analysisTypeMatch = analysisTypeFilter
      ? calibration.analysis_type &&
        (Array.isArray(calibration.analysis_type)
          ? analysis_type.some((filterType) =>
              (calibration.analysis_type as any[]).some((calType) => calType.id === filterType.id)
            )
          : analysis_type.some((at) => at.id === (calibration.analysis_type as any)?.id))
      : true;

    const partFilter = part && part.id;
    const partMatch = partFilter
      ? calibration.part && calibration.part.id === part.id
      : true;

    return dateInRange && equipmentMatch && analysisTypeMatch && partMatch;
  });
}

export const recalculateAndUpdateFunction = (updatedRows) => {
  const clonedUpdatedRows = JSON.parse(JSON.stringify(updatedRows)).filter(
    (row) => row.sqp_tsie_quenching !== "" && row.fa_exp !== ""
  );

  if (clonedUpdatedRows.length < 2) {
    return;
  }

  const puntosActualizados = clonedUpdatedRows.map((row) => ({
    x: parseFloat(row.sqp_tsie_quenching),
    y: parseFloat(row.fa_exp),
  }));

  if (puntosActualizados.length < 2) {
    return;
  }

  try {
    const { coeficientesNumericos, coeficientesFormateados, r2 } =
      recalcularCoeficientes(puntosActualizados);

    const u2relFaCalculado = calcularU2relFa(
      clonedUpdatedRows,
      coeficientesNumericos
    );

    const polinomioAjuste = `Fa calc = ${coeficientesFormateados[0]} + ${coeficientesFormateados[1]} * TSIE + ${coeficientesFormateados[2]} * TSIE^2 + ${coeficientesFormateados[3]} * TSIE^3 + ${coeficientesFormateados[4]} * TSIE^4`;

    const resultsData = {
      u2relFa: u2relFaCalculado,
      coefficients: {
        grade4: coeficientesFormateados[0], // A
        grade3: coeficientesFormateados[1], // B
        grade2: coeficientesFormateados[2], // C
        grade1: coeficientesFormateados[3], // D
        independent: coeficientesFormateados[4], // E
        r2: r2.toFixed(4),
      },
      polinomioAjuste,
    };

    return resultsData;
  } catch (error) {
    console.error("Error al recalcular coeficientes: ", error);
  }
};

export const getDefaultValues = async (
  calibration: CalibrationTritioResponse | undefined,
  user: UserType | undefined
) => {
  const today = new Date();

  const firstAnalysisType = Array.isArray(calibration?.analysis_type) 
    ? calibration?.analysis_type[0]?.codigo 
    : calibration?.analysis_type?.codigo;
  
  const hasRowCurvaAbs = hasRowCurva.includes(firstAnalysisType as KeysTabsAnalysis);
  
  // Determinar si tiene curva K40: aplica si contiene BT o BR
  const analysisTypeCodes = Array.isArray(calibration?.analysis_type) 
    ? calibration?.analysis_type.map(at => at.codigo)
    : [calibration?.analysis_type?.codigo];
  
  const hasRowsCurvaK40 = analysisTypeCodes.some(code => 
    code === KeysTabsAnalysis.BT || code === KeysTabsAnalysis.BR
  );

  let calibrationRows: any[] = [];
  let calibrationRowsK40: any[] = [];

  if (calibration && hasRowCurvaAbs) {
    calibrationRows = await mapCalibrationPreloadToRowsCurva(calibration?.id);
    // Si no hay datos del backend, inicializar con filas por defecto
    if (calibrationRows.length === 0) {
      const { initialRowsCurva } = await import('./curva.rows');
      calibrationRows = initialRowsCurva;
    }
  } else if (hasRowCurvaAbs) {
    // Nueva calibración: inicializar con filas por defecto
    const { initialRowsCurva } = await import('./curva.rows');
    calibrationRows = initialRowsCurva;
  }

  if (calibration && hasRowsCurvaK40) {
    calibrationRowsK40 = await mapCalibrationPreloadToRowsCurvaK40(
      calibration?.id
    );
    // Si no hay datos del backend, inicializar con filas por defecto
    if (calibrationRowsK40.length === 0) {
      const { initialRowsCurva } = await import('./curva.rows');
      calibrationRowsK40 = initialRowsCurva;
    }
  } else if (hasRowsCurvaK40) {
    // Nueva calibración: inicializar con filas por defecto
    const { initialRowsCurva } = await import('./curva.rows');
    calibrationRowsK40 = initialRowsCurva;
  }

  return {
    id: calibration?.id || undefined,
    analyst: calibration?.analyst || user,
    typeAnalysis: calibration?.analysis_type
      ? Array.isArray(calibration.analysis_type) 
        ? calibration.analysis_type.map(type => ({
            ...type,
            descripcion: type.codigo === KeysTabsAnalysis.BT
              ? "Beta Total"
              : type.codigo === KeysTabsAnalysis.BR
              ? "Beta Resto"
              : type.descripcion,
          }))
        : [{
            ...calibration.analysis_type,
            descripcion: calibration.analysis_type.codigo === KeysTabsAnalysis.BT
              ? "Beta Total"
              : calibration.analysis_type.codigo === KeysTabsAnalysis.BR
              ? "Beta Resto"
              : calibration.analysis_type.descripcion,
          }]
      : undefined,
    date: dayjs(calibration?.date || today),
    date_detector: dayjs(calibration?.date_detector || today),
    equipment: calibration?.equipment ? calibration?.equipment : undefined,
    ta0: calibration?.ta0 || "",
    tua0: calibration?.tua0 || "",
    tmpp: calibration?.tmpp || "",
    tmps: calibration?.tmps || "",
    tertiary_patron_mass: calibration?.tertiary_patron_mass || "",
    calibration_source_counting_time_without_extinguishing_agent:
      calibration?.calibration_source_counting_time_without_extinguishing_agent ||
      "",
    tsie_sqp_patron_without_agent_extintor:
      calibration?.tsie_sqp_patron_without_agent_extintor || "",
    tmpsfcal: calibration?.tmpsfcal || "",
    tdpmg: calibration?.tdpmg || "",
    tef: calibration?.tef || "",
    tcpmps: calibration?.tcpmps || "",
    tcal: calibration?.tcal || "",
    internal_comments: calibration?.internal_comments || "",
    modified_record: calibration?.modified_record || false,
    rowsCurva: calibrationRows,
    rowsCurvaK40: calibrationRowsK40,
    rowsCurvaPreload: calibrationRows,
    rowsCurvaK40Preload: calibrationRowsK40,
    uncertainty_calibration_source_counting_time_without_extinguishing_agent:
      calibration?.tfact_aa !== null && calibration?.tfact_aa
        ? toExponentialUppercase(calibration?.tfact_aa, 4)
        : "",
    uncertainty_calibration_source_counting_time_without_extinguishing_agent_k_40:
      calibration?.tfact_aa_k_40 !== null && calibration?.tfact_aa_k_40
        ? toExponentialUppercase(calibration?.tfact_aa_k_40, 4)
        : "",
    cpm_of_the_alpha_pattern_in_the_beta_win:
      calibration?.cpm_of_the_alpha_pattern_in_the_beta_win || "",
    cpm_of_the_alpha_pattern_in_the_alfa_win:
      calibration?.cpm_of_the_alpha_pattern_in_the_alfa_win || "",
    total_counting_time_alpha_pattern_in_total_detectors:
      calibration?.total_counting_time_alpha_pattern_in_total_detectors || "",
    vol_of_sec_in_calibration_source_at_0:
      calibration?.vol_of_sec_in_calibration_source_at_0,

    sec_pattern_activity_at_0_cal_s_at_count_d:
      calibration?.sec_pattern_activity_at_0_cal_s_at_count_d,
    cal_source_efficiency_at_0: calibration?.cal_source_efficiency_at_0,
    cpm_of_cal_source_with_quantity_at_0:
      calibration?.cpm_of_cal_source_with_quantity_at_0,
    cal_source_counting_time_to_0_in_total_detectors:
      calibration?.cal_source_counting_time_to_0_in_total_detectors,
    mass_of_the_torta_of_fc_with_the_lowest_amount:
      calibration?.mass_of_the_torta_of_fc_with_the_lowest_amount,
    efficiency_of_the_torta_of_fc_with_the_lowest_amount:
      calibration?.efficiency_of_the_torta_of_fc_with_the_lowest_amount,
    cpm_of_the_torta_of_fc_with_the_lowest_amount:
      calibration?.cpm_of_the_torta_of_fc_with_the_lowest_amount,
    total_counting_time_of_the_torta_of_fc_with_the_lowest_amount:
      calibration?.total_counting_time_of_the_torta_of_fc_with_the_lowest_amount,
    terror_k_40: calibration?.terror_k_40,
    coe0_k_40: calibration?.coe0_k_40,
    coe1_k_40: calibration?.coe1_k_40,
    coe2_k_40: calibration?.coe2_k_40,
    coe3_k_40: calibration?.coe3_k_40,
    coe4_k_40: calibration?.coe4_k_40,
    tnpoints_k_40: calibration?.tnpoints_k_40,
    tfact_aa: calibration?.tfact_aa,
    tfact_aa_k_40: calibration?.tfact_aa_k_40,
    identity_pattern_source: calibration?.identity_pattern_source,
    mean_efficiency: calibration?.mean_efficiency,
    detector: calibration?.detector,
    date_calibration_certificate: calibration?.date_calibration_certificate
      ? dayjs(calibration?.date_calibration_certificate || undefined)
      : undefined,
    part: calibration?.part || undefined,
    //results: setResults(calibrationRows),
  };
};

export const calculateFcCalcFinal = (
  coef_1: number,
  coef_2: number,
  coef_3: number,
  coef_4: number,
  coef_5: number,
  x: number
) => {
  return (
    coef_1 * Math.pow(x, 4) +
    coef_2 * Math.pow(x, 3) +
    coef_3 * Math.pow(x, 2) +
    coef_4 * x +
    coef_5
  );
};

export const calculateEfficiencyFinal = (
  rowsCurva: CurvaRow[],
  coeficients: MathArray
) => {
  const fixedCoefcient0 = parseFloat(
    coeficients[0][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient1 = parseFloat(
    coeficients[1][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient2 = parseFloat(
    coeficients[2][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient3 = parseFloat(
    coeficients[3][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient4 = parseFloat(
    coeficients[4][0].toExponential(COEFFICIENTS_DECIMALS)
  );

  const recalculatedRows = rowsCurva.map((row) => ({
    ...row,
    fc_calc:
      row.sqp_tsie_quenching != 0
        ? calculateFcCalcFinal(
            fixedCoefcient0, // A
            fixedCoefcient1, // B
            fixedCoefcient2, // C
            fixedCoefcient3, // D
            fixedCoefcient4, // E
            Number(row.sqp_tsie_quenching)
          )
        : 0,
  }));
  return recalculatedRows;
};
export const calculateEfficiencyAlphaFinal = (
  rowsCurva: CurvaRow[],
  coeficients: MathArray
) => {
  const fixedCoefcient0 = parseFloat(
    coeficients[0][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient1 = parseFloat(
    coeficients[1][0].toExponential(COEFFICIENTS_DECIMALS)
  );
  const fixedCoefcient2 = parseFloat(
    coeficients[2][0].toExponential(COEFFICIENTS_DECIMALS)
  );

  const recalculatedRows = rowsCurva.map((row) => ({
    ...row,
    fc_calc:
      row.sqp_tsie_quenching !== 0
        ? calculateFcCalcAlpha(
            fixedCoefcient0, // A
            fixedCoefcient1, // B
            fixedCoefcient2, // E
            Number(row.sqp_tsie_quenching)
          )
        : 0,
  }));
  return recalculatedRows;
};

export const categorizeCalibrationPatternSources = (
  sources: CalibrationPatternSourceType[]
) => {
  const calibrationPatternSourceToNew: CalibrationPatternSourceType[] = [];
  const calibrationPatternSourceToUpdate: CalibrationPatternSourceType[] = [];
  const calibrationPatternSourceToDelete: CalibrationPatternSourceType[] = [];

  sources.forEach((source) => {
    const hasValidIsotope = source.isotope !== "" && source.isotope !== null;
    const hasValidActivity =
      source.activity_bq !== "" && source.activity_bq !== null;

    if (typeof source.id === "string" && source.id.startsWith("new-")) {
      if (hasValidIsotope && hasValidActivity) {
        calibrationPatternSourceToNew.push(source);
      }
    } else if (typeof source.id === "number") {
      if (hasValidIsotope && hasValidActivity) {
        calibrationPatternSourceToUpdate.push(source);
      } else {
        calibrationPatternSourceToDelete.push(source);
      }
    }
  });

  return {
    calibrationPatternSourceToNew,
    calibrationPatternSourceToUpdate,
    calibrationPatternSourceToDelete,
  };
};

export const efficienciesIGMapping = (efficiency) => { 
  const result = {
    analyst: efficiency?.analyst?.id,
    date: efficiency?.date,
    equipment: efficiency?.equipment?.id,
    cs_source_geometry: efficiency?.cs_source_geometry?.id,
    internal_comments: efficiency.internal_comments,
    efficiency_type: efficiency.efficiency_type.key,
    analysis: efficiency.analysis.map(a => a.id),
    modified_record: !!efficiency.id || false,
    calibrationIG: efficiency.calibrationIG,
  };
  
  return result;
};
